import React from 'react';
import { visuallyHidden } from '@noths/polaris-client-styles';
import { priceFormatter } from '@noths/polaris-client-utils';
import { Icon } from '@noths/polaris-design-system-core-components';
import type { CurrencyCode } from '@noths/polaris-design-system-ooux-interfaces';

import * as styles from './ProductPrice.styles';

/**
 * Which colour the price and saving take. Undiscounted prices stay in the
 * default content colour; a discount is either good news about this product
 * (offer) or part of a sale event (sale).
 */
type PriceEmphasis = 'none' | 'offer' | 'sale';

const getEmphasis = (isDiscounted: boolean, promoteAsSale: boolean): PriceEmphasis => {
  if (!isDiscounted) {
    return 'none';
  }

  return promoteAsSale ? 'sale' : 'offer';
};

export interface ProductPriceDiscount {
  /**
   * The pre-discount price in minor units, where the surface has one. The PDP
   * quotes a saving the service gives it, which can arrive without a was-price
   * in the currency on display, so a percentage can stand on its own.
   */
  originalPrice?: number;
  /** The saving as a whole percentage of the original price. */
  percentage: number;
}

export interface ProductPriceProps {
  currencyCode: CurrencyCode;
  discount: ProductPriceDiscount | undefined;
  /** The price in minor units. */
  price: number;
  /**
   * Renders "From" on the line above the price, for a product whose options add
   * cost so the price shown is the cheapest way to buy it rather than the only one.
   */
  priceIsMinimum?: boolean;
  promoteAsSale: boolean;
  /** A slot laid out at the far end of the price row. The card puts its rating here. */
  trailing?: React.ReactNode;
  /**
   * Typographic scale for the price itself. 'md' is the card; 'lg' is a page that
   * leads with the price, such as the PDP. The saving and the original price are
   * the same size either way.
   */
  variant?: 'md' | 'lg';
}

/**
 * The price row and, for a discounted product, the saving beneath it.
 *
 * The saving is the percentage alone. The design also shows the money saved
 * beside it ("-17% · £5 off"), but the percentage on its own is agreed and the
 * design will be updated to match, so don't add the amount back.
 *
 * As an *offer* the original price comes first and the price the customer pays
 * is positive-coloured. As a *sale* the price the customer pays leads, marked
 * with a sale tag and in the negative colour, which is the language the site
 * uses for a sale event. The data is identical; only the emphasis changes.
 *
 * Shared by the card and the PDP so a customer meets one presentation of a
 * discount across a journey, per the global sale and offer switch.
 */
export const ProductPrice = ({
  currencyCode,
  discount,
  price,
  priceIsMinimum = false,
  promoteAsSale,
  trailing,
  variant = 'md',
}: ProductPriceProps) => {
  const emphasis = getEmphasis(Boolean(discount), promoteAsSale);
  const isSale = emphasis === 'sale';

  const currentPrice = (
    <span css={[styles.price, variant === 'lg' && styles.priceLarge]} data-testid="product-price">
      {priceFormatter(currencyCode, price)}
    </span>
  );

  const originalPrice = discount?.originalPrice !== undefined && (
    <>
      <span css={visuallyHidden}>Was</span>
      <s css={styles.originalPrice} data-testid="product-original-price">
        {priceFormatter(currencyCode, discount.originalPrice)}
      </s>
    </>
  );

  /* Its own line above the pair. Inline it would qualify whichever price it sat
     next to, which in the offer order is the price the customer does not pay. */
  const from = priceIsMinimum && <p css={styles.from}>From</p>;

  return (
    <>
      {from}
      <p css={styles.priceRow} data-emphasis={emphasis}>
        <span css={styles.prices}>
          {isSale && (
            <span css={styles.saleIcon}>
              <Icon aria-hidden data-icon="sale" name="sale" size="sm" />
            </span>
          )}
          {isSale ? (
            <>
              {currentPrice}
              {originalPrice}
            </>
          ) : (
            <>
              {originalPrice}
              {currentPrice}
            </>
          )}
        </span>
        {trailing}
      </p>
      {discount && discount.percentage > 0 && (
        <p css={styles.discount} data-emphasis={emphasis} data-testid="product-discount">
          {`-${discount.percentage}%`}
        </p>
      )}
    </>
  );
};

ProductPrice.displayName = 'ProductPrice';
